import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type {
  BaseQueryApi,
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  SerializeQueryArgs,
} from "@reduxjs/toolkit/query";
import { defaultSerializeQueryArgs } from "@reduxjs/toolkit/query";
import {
  load,
  AdminEnv,
  ClientPwaEnv,
  WeightControlEnv,
  GovDashPwaEnv,
  InspectEnv,
} from "@racoon/env";
import { connectBaseQuery } from "./connect/baseQuery";
import { normalizeBigInt } from "./shared/lib/normalizeBigInt";
import { refreshSession } from "./connect/refreshInterceptor";
import {
  clearAuthTokens,
  getAuthToken,
  getAuthorizationHeaderValue,
  redirectToLoginIfNeeded,
  resolveSelectedCompanyId,
} from "./shared/lib/authSession";
// Normalize BigInt → string for cache key generation. Works on a COPY of queryArgs.
// Note: rtkqLoadingMiddleware also normalizes originalArgs (the original object)
// before storing in Redux state to prevent serialization issues.
const stableSerializeQueryArgs: SerializeQueryArgs<unknown> = ({
  queryArgs,
  endpointDefinition,
  endpointName,
}) =>
  defaultSerializeQueryArgs({
    queryArgs: normalizeBigInt(queryArgs),
    endpointDefinition,
    endpointName,
  });

const isAdminApp =
  typeof window !== "undefined" &&
  (window.location?.pathname?.includes("admin") || window.location?.hostname?.includes("admin"));

const isClientPwaApp =
  typeof window !== "undefined" &&
  (window.location?.pathname?.includes("client") ||
    window.location?.hostname?.includes("client") ||
    window.location?.hostname?.includes("local-client"));

const isWeightControlApp =
  typeof window !== "undefined" &&
  (window.location?.pathname?.includes("weight") ||
    window.location?.hostname?.includes("weight") ||
    window.location?.hostname?.includes("local-weight"));

const isGovDashApp =
  typeof window !== "undefined" &&
  (window.location?.pathname?.includes("gov") ||
    window.location?.hostname?.includes("gov") ||
    window.location?.hostname?.includes("local-gov"));

const isInspectApp =
  typeof window !== "undefined" &&
  (window.location?.pathname?.includes("inspect") ||
    window.location?.hostname?.includes("inspect") ||
    window.location?.hostname?.includes("local-inspect"));

// Debug logging
if (typeof window !== "undefined") {
  console.log("Environment detection:", {
    isAdminApp,
    isClientPwaApp,
    isWeightControlApp,
    isGovDashApp,
    isInspectApp,
    hostname: window.location?.hostname,
    pathname: window.location?.pathname,
  });
}

const env = load(
  isAdminApp
    ? AdminEnv
    : isClientPwaApp
      ? ClientPwaEnv
      : isWeightControlApp
        ? WeightControlEnv
        : isGovDashApp
          ? GovDashPwaEnv
          : isInspectApp
            ? InspectEnv
            : AdminEnv,
);

// Debug logging
if (typeof window !== "undefined") {
  console.log("Loaded environment:", env);
}

export type CompanyExtraOptions = {
  appendCompanyId?: boolean | string;
};

type RootStateLike = {
  company?: {
    selectedCompanyId?: number | null;
  };
};

function trimTrailingSlash(s: string) {
  return s.replace(/\/+$/, "");
}

function appendPathId(args: string | FetchArgs, key: string, value: number): string | FetchArgs {
  if (typeof args === "string") {
    const [url, queryString] = args.split("?");
    const params = new URLSearchParams(queryString);
    params.set(key, String(value));
    return `${url}?${params.toString()}`;
  }
  let newParams: URLSearchParams;

  if (args.params instanceof URLSearchParams) {
    newParams = new URLSearchParams(args.params);
  } else if (args.params) {
    newParams = new URLSearchParams();
    Object.entries(args.params).forEach(([k, v]) => {
      if (Array.isArray(v)) {
        v.forEach((item) => newParams.append(k, String(item)));
      } else if (v !== undefined && v !== null) {
        newParams.append(k, String(v));
      }
    });
  } else {
    newParams = new URLSearchParams();
  }
  newParams.set(key, String(value));

  return {
    ...args,
    params: newParams,
  };
}

const rawBase = fetchBaseQuery({
  baseUrl: trimTrailingSlash(env.VITE_API_URL || "/"),
  prepareHeaders: (headers, api) => {
    // Auth
    const authHeader = getAuthorizationHeaderValue();
    if (authHeader) headers.set("authorization", authHeader);

    // Selected company header (used by many endpoints server-side)
    const state = api.getState() as RootStateLike;
    const orgId = resolveSelectedCompanyId(state);
    if (orgId) headers.set("x-organization-id", String(orgId));

    return headers;
  },
});

/** A 401 that means the session itself was refused, not a wrong password or OTP. */
function isSessionRejected(res: { error?: FetchBaseQueryError }): boolean {
  if (res.error?.status !== 401) return false;

  const errorMessage = (res.error.data as any)?.message;
  return (
    errorMessage !== "Current password is incorrect" &&
    errorMessage !== "OTP code is incorrect or expired"
  );
}

const baseQueryWithCompany: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  CompanyExtraOptions
> = async (args, api: BaseQueryApi, extraOptions?: CompanyExtraOptions) => {
  let finalArgs = args;
  if (extraOptions?.appendCompanyId) {
    const state = api.getState() as RootStateLike;
    const orgId = resolveSelectedCompanyId(state);

    if (!orgId) {
      return {
        error: {
          status: 400,
          data: {
            code: "ERROR_CODE_COMPANY_NOT_SELECTED",
            message: "ERROR_CODE_COMPANY_NOT_SELECTED",
          },
        } as FetchBaseQueryError,
      };
    }
    const paramName =
      typeof extraOptions.appendCompanyId === "string"
        ? extraOptions.appendCompanyId
        : "organization_id";
    finalArgs = appendPathId(args, paramName, orgId);
  }

  let sentToken = getAuthToken();
  let res = await rawBase(finalArgs, api, extraOptions ?? {});
  // Handle 401 Unauthorized - refresh the session once, then redirect to login
  // Don't logout if it's just an incorrect password attempt
  if (isSessionRejected(res)) {
    // Another tab may already have stored a fresh token; otherwise refresh.
    const storedToken = getAuthToken();
    const tokenChanged = storedToken !== null && storedToken !== sentToken;
    if (tokenChanged || (await refreshSession())) {
      sentToken = getAuthToken();
      res = await rawBase(finalArgs, api, extraOptions ?? {});
    }
  }

  // As in the Connect base query: a token stored while the call was out (a
  // refresh or login, possibly in another tab) is not this failure's to wipe.
  if (isSessionRejected(res) && getAuthToken() === sentToken) {
    clearAuthTokens();
    redirectToLoginIfNeeded();
  }

  return res;
};

export const baseApi = createApi({
  reducerPath: "baseApi",
  baseQuery: baseQueryWithCompany,
  tagTypes: [
    "Company",
    "Employee",
    "Order",
    "Job",
    "Product",
    "Supply",
    "Expense",
    "PaymentTransaction",
    "Writeoff",
    "Goods",
    "Truck",
    "DriverOnShift",
    "PickupLocation",
    "Chats",
    "ContainerType",
    "Container",
    "ScaleRecord",
    "Contact",
  ],
  endpoints: () => ({}),
});

export const connectBaseApi = createApi({
  reducerPath: "connectBaseApi",
  baseQuery: connectBaseQuery(),
  serializeQueryArgs: stableSerializeQueryArgs,
  tagTypes: [
    "Auth",
    "Truck",
    "TruckType",
    "Driver",
    "DriverOnShift",
    "DriverIncident",
    "Depot",
    "DisposalSite",
    "Location",
    "GovGeoAccess",
    "PickupLocation",
    "PickupLocationGroup",
    "ServiceProvider",
    "ServiceReceiver",
    "SuspiciousLocation",
    "SuspiciousLocationAnalytics",
    "DashboardAnalytics",
    "Contracts",
    "CustomGroups",
    "GroupGrants",
    "GroupMemberships",
    "Employee",
    "Company",
    "Order",
    "Container",
    "ContainerType",
    "ContainerGroup",
    "ContainerGroupSchedule",
    "Contact",
    "OrgSettings",
    "Blacklist",
    "ShiftPlan",
    "ScaleRecord",
    "Team",
    "Routes",
    "Route",
    "AuditLog",
    "OrganizationLabel",
  ],
  endpoints: () => ({}),
});
